import {
	Card,
	CardMedia,
	Chip,
	Divider,
	Grid,
	styled,
	Typography,
} from "@mui/material";
import { blue, green, orange, pink, purple } from "@mui/material/colors";
import { getPreviewUrl } from "api/base";
import { Activity } from "api/types";
import clsx from "clsx";
import { PreviewActivity } from "components/PreviewActivity";
import { StyledButton } from "components/StyledButton";
import { useEffect, useState } from "react";

const PREFIX = "ActivityCard";

const classes = {
	root: `${PREFIX}-root`,
	chip: `${PREFIX}-chip`,
	section1: `${PREFIX}-section1`,
	section2: `${PREFIX}-section2`,
	section3: `${PREFIX}-section3`,
	previewBtn: `${PREFIX}-previewBtn`,
	quiz: `${PREFIX}-quiz`,
	animation: `${PREFIX}-animation`,
	game: `${PREFIX}-game`,
	book: `${PREFIX}-book`,
	song: `${PREFIX}-song`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
	[`&.${classes.root}`]: {
		padding: theme.spacing(0.5),
		minHeight: "400px",
	},

	[`& .${classes.chip}`]: {
		margin: theme.spacing(0.5),
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
	},

	[`& .${classes.section1}`]: {
		margin: theme.spacing(3, 2),
		marginBottom: 0,
	},

	[`& .${classes.section2}`]: {
		margin: theme.spacing(2),
		width: "auto",
	},

	[`& .${classes.section3}`]: {
		padding: theme.spacing(1),
		maxHeight: "min-content",
	},
	[`& .${classes.song}`]: {
		backgroundColor: purple[400],
	},
	[`& .${classes.game}`]: {
		backgroundColor: orange[400],
	},
	[`& .${classes.quiz}`]: {
		backgroundColor: green[400],
	},
	[`& .${classes.animation}`]: {
		backgroundColor: blue[400],
	},
	[`& .${classes.book}`]: {
		backgroundColor: pink[400],
	},
}));

export interface ActivityCardProps {
	activity: Activity;
	handler: (item: Activity) => void;
	selected: Activity[];
}

export const ActivityCard = (props: ActivityCardProps): JSX.Element => {
	const { activity, handler, selected } = props;
	const [added, setAdded] = useState(false);
	const baseURL = getPreviewUrl(activity.questId);

	useEffect(() => {
		const activityExists = selected.some(
			(item) =>
				item.activityType === activity.activityType &&
				item.name === activity.name,
		);
		if (activityExists && !added) {
			setAdded(true);
		}
		if (!activityExists && added) {
			setAdded(false);
		}
	}, [activity, added, selected]);

	return (
		<Card>
			<CardMedia component="img" height="188" image={baseURL} alt="preview" />
			<StyledGrid
				container
				justifyContent="space-between"
				direction="column"
				className={classes.root}
			>
				<Grid item className={classes.section1}>
					<Grid
						container
						alignItems="flex-start"
						justifyContent="space-between"
					>
						<Grid item xs={6}>
							<Typography gutterBottom variant="h4">
								{activity.name}
							</Typography>
						</Grid>
						<Grid item>
							<PreviewActivity handler={handler} activity={activity} />
						</Grid>
					</Grid>
					<Grid container justifyContent="space-between">
						<Typography variant="subtitle2">{activity.realm}</Typography>
					</Grid>
				</Grid>
				<Grid item>
					<Divider variant="middle" />
					<Grid
						container
						justifyContent="space-between"
						className={classes.section2}
					>
						<Chip
							label={activity.activityType}
							className={clsx({
								[classes.animation]: activity.activityType === "animation",
								[classes.game]: activity.activityType === "game",
								[classes.book]: activity.activityType === "book",
								[classes.song]: activity.activityType === "song",
								[classes.quiz]: activity.activityType === "quiz",
								[classes.chip]: activity.activityType,
							})}
						/>
						<Typography variant="subtitle2"> Grade 4,5,6 </Typography>
					</Grid>
					<Grid container justifyContent="center" className={classes.section3}>
						<StyledButton
							color="secondary"
							variant="contained"
							data-testid="add-act-btn"
							fullWidth
							onClick={() => handler(activity)}
							disabled={added}
						>
							Add to assignment
						</StyledButton>
					</Grid>
				</Grid>
			</StyledGrid>
		</Card>
	);
};
